import { Show } from 'solid-js';
import { HeroCviImage } from './hero-cvi-types';
import {
    LARGER_IMAGE_SIZES,
    LARGER_IMAGE_WIDTHS,
    useHeroImageBuilder,
} from './image-helpers';

type HeroCviSingleImageProps = {
    image?: HeroCviImage;
    captionName?: string;
    captionRole?: string;
    captionText?: string;
};

/**
 * Single-image hero variant — one fixed image at 16:9 with an optional caption row underneath
 * (name · role · text). No carousel/auto-advance machinery; if the consumer wants a carousel,
 * use `<HeroCviCarousel>` instead.
 */
export const HeroCviSingleImage = (props: HeroCviSingleImageProps) => {
    const { buildImageSrc, buildImageSrcSet, imageStyle } = useHeroImageBuilder();

    return (
        <div class="flex flex-col gap-cvi-3">
            <div class="relative rounded-cvi-md overflow-hidden bg-cvi-bg-neutral-subtle aspect-[16/9]">
                <Show when={props.image?.url}>
                    <img
                        src={buildImageSrc(props.image, 1200)}
                        srcset={buildImageSrcSet(props.image, LARGER_IMAGE_WIDTHS)}
                        sizes={LARGER_IMAGE_SIZES}
                        alt={props.image?.altText ?? ''}
                        class="size-full object-cover"
                        style={imageStyle(props.image)}
                        {...!props.image?.altText ? { role: 'presentation' } : {}}
                    />
                </Show>
            </div>
            <Show when={props.captionName || props.captionRole || props.captionText}>
                <p class="body-sm font-cvi-regular text-cvi-fg-default m-0">
                    <Show when={props.captionName}>
                        <span class="font-cvi-medium">{props.captionName}</span>
                    </Show>
                    <Show when={props.captionName && props.captionRole}>
                        <span aria-hidden="true">{' | '}</span>
                        <span class="sr-only">, </span>
                    </Show>
                    <Show when={props.captionRole}>
                        <span>{props.captionRole}</span>
                    </Show>
                    <Show when={(props.captionName || props.captionRole) && props.captionText}>
                        <span
                            class="inline-block size-cvi-2 mx-cvi-4 rounded-cvi-round bg-cvi-fg-brand align-middle"
                            aria-hidden="true"
                        />
                        <span class="sr-only">. </span>
                    </Show>
                    <Show when={props.captionText}>
                        <span>{props.captionText}</span>
                    </Show>
                </p>
            </Show>
        </div>
    );
};
